import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useEffect } from "react";
import { Platform, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text, TextInput } from "react-native-paper";

import type { QuestionComponentProps } from "./types";

import { RecordingStripe } from "@/components/recorder/RecordingStripe";
import { RingHalo } from "@/components/recorder/RingHalo";
import { ThinkingStripe } from "@/components/recorder/ThinkingStripe";
import { useQuestionRecorder, type FloatingMicField } from "@/hooks/useQuestionRecorder";
import { useResolvedReducedMotion } from "@/hooks/useResolvedReducedMotion";
import { colors, fonts, gradients, radius } from "@/lib/theme";

interface TextQuestionProps extends QuestionComponentProps {
    onRecordingBusyChange?: (busy: boolean) => void;
    recorderMode?: "question" | "section";
    saveLabel?: string;
}

interface CardProps extends TextQuestionProps {
    long: boolean;
}

/**
 * Flat-section text question — card with the label inside, a textarea, and
 * an in-card 48px gradient mic in the footer. Replaces the floating-mic
 * pattern: each TextQuestion now owns its own useQuestionRecorder instance.
 *
 * Footer states (one rendered at a time):
 *   - idle      → word-count (left) + 48px gradient mic (right)
 *   - recording → RecordingStripe (white pill: cancel × · red dot · 22-bar
 *                 LiveWaveform · timer · 40px red stop)
 *   - thinking  → ThinkingStripe (orb · "Transcribing" + dots · "Adding to …")
 *
 * Tap the mic when state is idle to start. Tap stop on the stripe to stop.
 * The textarea dims to opacity 0.35 while recorder is non-idle so accidental
 * typing during transcription doesn't clobber the streamed transcript.
 */
function TextQuestionCard({
    question,
    value,
    onChange,
    readOnly,
    onFocus,
    onBlur,
    long,
    visitId,
    sectionId,
    onRecordingBusyChange,
    recorderMode = "question",
    saveLabel = "saved"
}: CardProps) {
    const field: FloatingMicField = {
        id: String(question.id),
        label: question.text
    };

    const recorder = useQuestionRecorder({
        mode: recorderMode,
        visitId: visitId ?? "",
        sectionId: sectionId ?? "",
        onTranscribed: (text) => {
            // Append the transcript to the current value with a single space
            // separator. Mirrors the prior FloatingMicRecorder flow: the
            // section host used to write into useFormContext().setAnswer here.
            const next = (value ?? "").trim();
            onChange(next.length === 0 ? text : `${next} ${text}`);
        }
    });

    const phase = recorder.state.status;
    const isRecording = phase === "recording";
    const isBusy = phase === "recording" || phase === "thinking";

    useEffect(() => {
        onRecordingBusyChange?.(isBusy);
    }, [isBusy, onRecordingBusyChange]);

    return (
        <View
            style={[
                styles.card,
                isBusy
                    ? { borderColor: "rgba(69,210,176,0.5)", borderWidth: 1.5 }
                    : { borderColor: colors.surfaceBorder, borderWidth: 1 }
            ]}
        >
            {/* No in-card label here — SpecRow renders the question label
                above the card now. Previously this label duplicated the
                SpecRow label, producing two identical lines on screen. */}
            <TextInput
                accessibilityLabel={question.text}
                mode="outlined"
                value={value ?? ""}
                onChangeText={onChange}
                disabled={readOnly || isBusy}
                placeholder={question.hintText ?? "Tap to type…"}
                onFocus={onFocus}
                onBlur={onBlur}
                multiline
                numberOfLines={long ? 6 : 4}
                style={[
                    styles.textarea,
                    { minHeight: long ? 150 : 100 },
                    isBusy && styles.textareaDimmed
                ]}
                outlineStyle={styles.textareaOutline}
            />

            {/* The dictation footer belongs to a nurse documenting a visit: the
                mic records against a visit section, and the save label reports the
                section autosave. With no visit (the patient questionnaire renders
                this card too) there is nothing to record into and nothing
                autosaving, so the footer is not drawn at all. */}
            {visitId && sectionId ? (
                <>
                    <View style={styles.footer}>
                        {isRecording ? (
                            <RecordingStripe
                                seconds={recorder.seconds}
                                meter={recorder.meter}
                                reduced={false}
                                onCancel={recorder.cancel}
                                onStop={() => {
                                    void recorder.stop();
                                }}
                            />
                        ) : recorder.state.status === "thinking" ? (
                            <ThinkingStripe
                                fieldLabel={recorder.state.field.label}
                                reduced={false}
                            />
                        ) : (
                            <>
                                <Text style={styles.wordCount}>
                                    {(value ?? "").split(/\s+/).filter(Boolean).length} words ·{" "}
                                    {saveLabel}
                                </Text>
                                <InCardMic
                                    disabled={readOnly === true}
                                    onPress={() => {
                                        void recorder.start(field);
                                    }}
                                />
                            </>
                        )}
                    </View>
                    {recorder.state.status === "error" && (
                        <Text
                            accessibilityRole="alert"
                            style={{ color: colors.error600, fontFamily: fonts.regular }}
                        >
                            {recorder.state.error.msg}
                        </Text>
                    )}
                </>
            ) : null}
        </View>
    );
}

interface InCardMicProps {
    disabled: boolean;
    onPress: () => void;
}

/**
 * 48px brand-gradient mic that lives in the bottom-right of every text card.
 * Reuses the live app's RingHalo for the pulsing halo behind it. Mounts the
 * halo only after `useResolvedReducedMotion` resolves to avoid the stale-
 * placeholder bug class documented in RingHalo.tsx.
 */
function InCardMic({ disabled, onPress }: InCardMicProps) {
    const motion = useResolvedReducedMotion();
    return (
        <TouchableOpacity
            onPress={onPress}
            disabled={disabled}
            activeOpacity={0.85}
            accessibilityRole="button"
            accessibilityLabel="Record voice note"
            style={styles.micHit}
        >
            <View style={styles.micHost}>
                {motion.resolved ? <RingHalo reduced={motion.reduced} /> : null}
                <LinearGradient
                    colors={[...gradients.brand]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={[styles.micCircle, disabled && styles.micCircleDisabled]}
                >
                    <MaterialCommunityIcons name="microphone" size={22} color="#fff" />
                </LinearGradient>
            </View>
        </TouchableOpacity>
    );
}

export function TextShortQuestion(props: TextQuestionProps) {
    return <TextQuestionCard {...props} long={false} />;
}

export function TextLongQuestion(props: TextQuestionProps) {
    return <TextQuestionCard {...props} long={true} />;
}

const styles = StyleSheet.create({
    card: {
        backgroundColor: "#fff",
        borderRadius: 18,
        padding: 16,
        gap: 12
    },
    label: {
        fontFamily: fonts.extraBold,
        fontSize: 16,
        color: colors.ink,
        letterSpacing: -0.2,
        lineHeight: 22
    },
    required: {
        color: colors.error500
    },
    textarea: {
        backgroundColor: colors.gray100,
        // Cap on Android: Paper TextInput multiline + numberOfLines ignores
        // numberOfLines for layout and grows unbounded when long transcripts
        // stream in, pushing the card layout. iOS already scrolls internally.
        maxHeight: 240,
        fontSize: 14.5
    },
    textareaDimmed: {
        opacity: 0.35
    },
    textareaOutline: {
        borderRadius: radius.md,
        borderWidth: 1.5,
        borderColor: colors.border
    },
    footer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        minHeight: 48
    },
    wordCount: {
        flexShrink: 0,
        fontFamily: fonts.extraBold,
        fontSize: 10.5,
        color: colors.textTertiary,
        letterSpacing: 0.6,
        textTransform: "uppercase"
    },
    micHit: {
        width: 48,
        height: 48,
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible"
    },
    micHost: {
        width: 48,
        height: 48,
        overflow: "visible",
        alignItems: "center",
        justifyContent: "center"
    },
    micCircle: {
        width: 48,
        height: 48,
        borderRadius: 24,
        alignItems: "center",
        justifyContent: "center",
        ...Platform.select({
            ios: {
                shadowColor: "#E748F5",
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.3,
                shadowRadius: 14
            },
            android: { elevation: 6 },
            default: {}
        })
    },
    micCircleDisabled: {
        opacity: 0.5
    }
});
